import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    UpOutlined,
} from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import cn from 'classnames';

import { useAntMessage } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { useAppDispatch } from '@/shared/lib/store';
import { Button } from '@/shared/ui/button';
import { Popover } from '@/shared/ui/popover';

import {
    useDeletePatternWatchlistMutation,
    useGetPatternWatchlistsQuery,
    useUpdatePatternWatchlistMutation,
} from '../../api/pattern-watchlist.api';
import { useSelectedPatternWatchlistId } from '../../model/selectors/pattern-watchlist-selected';
import { patternWatchlistActions } from '../../model/slices/pattern-watchlist.slice';
import { type PatternWatchlist } from '../../model/types/pattern-watchlist';
import { CreatePatternWatchlist } from '../create-pattern-watchlist/create-pattern-watchlist';
import { CurrentPatternWatchlist } from '../current-pattern-watchlist/current-pattern-watchlist';
import type { FormValues } from '../pattern-watchlist-form/pattern-watchlist-form';
import { PatternWatchlistModal } from '../pattern-watchlist-modal/pattern-watchlist-modal';

import cls from './pattern-watchlist-selector.module.css';

export const PatternWatchlistSelector = memo(() => {
    const dispatch = useAppDispatch();
    const { success } = useAntMessage();
    const selectedPatternWatchlistId = useSelectedPatternWatchlistId();
    const { data: watchlists = [] } = useGetPatternWatchlistsQuery();
    const [isWatchlistSelectorOpen, setIsWatchlistSelectorOpen] =
        useState(false);
    const [deleteWatchlist, deleteWatchlistResult] =
        useDeletePatternWatchlistMutation();
    const [
        updateWatchlist,
        { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess, reset },
    ] = useUpdatePatternWatchlistMutation();
    const { t } = useTranslation('pattern-watchlist');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedWatchlist, setSelectedWatchlist] = useState<
        PatternWatchlist | undefined
    >();
    const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);

    const onSelect = useCallback(
        (watchlist: PatternWatchlist) => {
            if (selectedPatternWatchlistId !== watchlist.id) {
                dispatch(
                    patternWatchlistActions.setSelectedPatternWatchlistId(
                        watchlist.id,
                    ),
                );
            }

            setIsWatchlistSelectorOpen(false);
        },
        [dispatch, selectedPatternWatchlistId],
    );

    const isWatchlistDeleting = useCallback(
        (watchlistId: number) =>
            (deleteWatchlistResult.isLoading &&
                deleteWatchlistResult.originalArgs === watchlistId) ||
            pendingDeleteIds.includes(watchlistId),
        [deleteWatchlistResult, pendingDeleteIds],
    );

    const handleDelete = useCallback(
        async (watchlist: PatternWatchlist) => {
            try {
                setPendingDeleteIds((prev) => [...prev, watchlist.id]);

                if (selectedPatternWatchlistId === watchlist.id) {
                    dispatch(
                        patternWatchlistActions.resetSelectedPatternWatchlistId(),
                    );
                }

                await deleteWatchlist(watchlist.id);

                success?.({
                    content: t('deleteSuccess'),
                });

                setPendingDeleteIds((prev) =>
                    prev.filter((pendingId) => pendingId !== watchlist.id),
                );
            } catch (error) {
                logError('Failed to delete watchlist:', error);
                setPendingDeleteIds((prev) =>
                    prev.filter((pendingId) => pendingId !== watchlist.id),
                );
            }
        },
        [t, deleteWatchlist, dispatch, selectedPatternWatchlistId, success],
    );

    const onOpenModal = useCallback(
        (watchlist: PatternWatchlist) => {
            setIsWatchlistSelectorOpen(false);
            setSelectedWatchlist(watchlist);
            setIsModalOpen(true);
            reset();
        },
        [reset, setIsWatchlistSelectorOpen],
    );

    const onCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedWatchlist(undefined);
        reset();
    }, [reset]);

    const onSubmit = useCallback(
        async (values: FormValues) => {
            if (!selectedWatchlist?.id) return;

            try {
                await updateWatchlist({
                    id: selectedWatchlist.id,
                    name: values.name,
                });

                success?.({
                    content: t('updateSuccess'),
                });

                onCloseModal();
            } catch (error) {
                logError('Failed to update watchlist:', error);
            }
        },
        [selectedWatchlist, updateWatchlist, success, t, onCloseModal],
    );

    const getFormData = useCallback(
        (watchlist: PatternWatchlist | undefined): FormValues | undefined => {
            if (!watchlist) return undefined;

            return {
                id: watchlist.id,
                name: watchlist.name,
            };
        },
        [],
    );

    return (
        <div className={cls.container}>
            <Popover
                content={
                    <>
                        <div className={cls.watchlists}>
                            <Typography.Text>{t('savedLists')}</Typography.Text>
                            {watchlists.map((watchlist) => (
                                <Flex
                                    key={watchlist.id}
                                    align="center"
                                    className={cn(cls.patternWatchlistItem, {
                                        [cls.selected]:
                                            selectedPatternWatchlistId ===
                                            watchlist.id,
                                    })}
                                    gap={8}
                                    justify="space-between"
                                    onClick={() => onSelect(watchlist)}
                                >
                                    <Typography.Text>
                                        {watchlist.name}
                                    </Typography.Text>

                                    <Flex gap={8}>
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onOpenModal(watchlist);
                                            }}
                                            variant="ghost"
                                        />

                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            isLoading={isWatchlistDeleting(
                                                watchlist.id,
                                            )}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(watchlist);
                                            }}
                                            variant="ghost"
                                        />
                                    </Flex>
                                </Flex>
                            ))}
                        </div>
                        <div className={cls.separator} />
                        <div className={cls.padding}>
                            <CreatePatternWatchlist />
                        </div>
                    </>
                }
                onOpenChange={setIsWatchlistSelectorOpen}
                open={isWatchlistSelectorOpen}
                trigger={
                    <div className={cls.watchlistTrigger}>
                        <CurrentPatternWatchlist />
                        {isWatchlistSelectorOpen ? (
                            <UpOutlined className={cls.watchlistTriggerIcon} />
                        ) : (
                            <DownOutlined
                                className={cls.watchlistTriggerIcon}
                            />
                        )}
                    </div>
                }
            />

            <PatternWatchlistModal
                data={getFormData(selectedWatchlist)}
                isLoading={isUpdateLoading}
                isOpen={isModalOpen}
                isSuccess={isUpdateSuccess}
                onClose={onCloseModal}
                onSubmit={onSubmit}
            />
        </div>
    );
});

PatternWatchlistSelector.displayName = 'PatternWatchlistSelector';
